import { FunctionComponent } from "react";

import { TabChip } from "@/components";
import { Tooltip } from "@/components/Tooltip/Tooltip";

import { List } from "../../schema/list";

import { Tabs } from "./unassignedTabs";

interface TabCountChipsProps {
	tab: Tabs;
	isActive: boolean;
	/** Unscoped lean list (carries the per-status counts shown on each tab). */
	list?: List;
	/** Live "Unsure" (excluded) count for the New tab, kept in sync by the table. */
	unsureCount: number;
}

/**
 * Count chip(s) rendered next to a status tab label. The New (UNASSIGNED) tab shows up to three
 * chips (Found / Unsure / Not found); the others show a single count. Extracted from
 * `Unassigned.tsx` to keep that file under the size ratchet.
 */
export const TabCountChips: FunctionComponent<TabCountChipsProps> = ({
	tab,
	isActive,
	list,
	unsureCount,
}) => {
	if (tab === Tabs.UNASSIGNED && list?.draftCount && list.draftCount > 0) {
		return (
			<>
				<TabChip active={isActive} label={`${list.draftCount}`} />
				{unsureCount > 0 ? (
					<Tooltip
						content={
							<p className="h-10">
								All leads that have been excluded from the list, because they don&apos;t fit the ICP
								qualification criteria or lack the required information
							</p>
						}
					>
						<TabChip active={isActive} color label={`${unsureCount}`} />
					</Tooltip>
				) : null}
				{list?.maybeCount && list.maybeCount > 0 ? (
					<Tooltip
						content={
							<p className="h-10">All leads or companies that we were not able to identify.</p>
						}
					>
						<TabChip active={isActive} color label={`${list.maybeCount}`} />
					</Tooltip>
				) : null}
			</>
		);
	}

	if (tab === Tabs.SAVED_FOR_LATER && list?.savedForLaterCount && list.savedForLaterCount > 0) {
		return <TabChip active={isActive} label={`${list.savedForLaterCount}`} />;
	}

	if (tab === Tabs.ASSIGNED && list?.assignedCount && list.assignedCount > 0) {
		return <TabChip active={isActive} label={`${list.assignedCount}`} />;
	}

	if (tab === Tabs.ARCHIVED && list?.archivedCount && list.archivedCount > 0) {
		return <TabChip active={isActive} label={`${list.archivedCount}`} />;
	}

	if (tab === Tabs.UNDER_REVIEW && list?.underReviewCount && list.underReviewCount > 0) {
		return <TabChip active={isActive} label={`${list.underReviewCount}`} />;
	}

	if (tab === Tabs.TRACKED_PROFILE && list?.trackedProfileCount && list.trackedProfileCount > 0) {
		return <TabChip active={isActive} label={`${list.trackedProfileCount}`} />;
	}

	if (
		tab === Tabs.TRACKED_PROFILE &&
		list?.trackerOrganizationCount &&
		list.trackerOrganizationCount > 0
	) {
		return <TabChip active={isActive} label={`${list.trackerOrganizationCount}`} />;
	}

	if (tab === Tabs.COMPANIES && list?.companiesCount && +list.companiesCount > 0) {
		return <TabChip active={isActive} label={`${list.companiesCount}`} />;
	}

	return null;
};
